import { isAuthCredentialError } from "./authErrors";

// Grace window after a successful refresh: requests that were already in
// flight on the stale token can report their terminal 401 afterwards — a
// query's onError only fires once its whole retry chain is exhausted, and
// invalidateQueries dedupes onto in-flight fetches instead of restarting
// them. Those stragglers get refetched with the fresh token anyway, so only
// a 401 clearly after the burst proves the refreshed session is dead.
const RECOVERY_GRACE_MS = 30_000;

/**
 * One-shot recovery for a stale/revoked auth session, meant to be called from
 * the global QueryCache error handler. On the first credential failure it
 * forces a token refresh: a still-refreshable session heals silently
 * (onRecovered refetches everything); a dead one is signed out so the login
 * redirect replaces otherwise-permanent loading/error screens. Further
 * credential failures while the refresh is in flight are swallowed (a dying
 * session fails many queries at once), as are stragglers from the same burst
 * landing within the grace window after a successful refresh.
 *
 * The one-shot state lives for the page lifetime, which outlives an SPA
 * sign-out/sign-in cycle — call reset() whenever the auth user changes so a
 * new session starts with a fresh recovery attempt instead of inheriting a
 * spent one (which would sign it out on its first 401 without a refresh).
 */
export function createAuthSessionRecovery(deps: {
  /** Force-refresh the ID token, e.g. () => auth.currentUser.getIdToken(true) */
  refreshToken: () => Promise<unknown>;
  /** Called after a successful refresh, e.g. cancel + invalidate all queries */
  onRecovered: () => void;
  signOut: () => void;
  /** When provided, errors are ignored while it returns false (signed out) */
  hasSession?: () => boolean;
  /** Injectable clock for tests; defaults to Date.now */
  now?: () => number;
}): { onError: (error: unknown) => void; reset: () => void } {
  const now = deps.now ?? Date.now;
  let state: "idle" | "recovering" | "attempted" = "idle";
  let recoveredAt: number | null = null;

  return {
    reset: () => {
      state = "idle";
      recoveredAt = null;
    },
    onError: (error: unknown) => {
      if (!isAuthCredentialError(error)) return;
      if (deps.hasSession && !deps.hasSession()) return;
      if (state === "recovering") return;
      if (state === "attempted") {
        if (recoveredAt !== null && now() - recoveredAt < RECOVERY_GRACE_MS) {
          return;
        }
        deps.signOut();
        return;
      }
      state = "recovering";
      deps
        .refreshToken()
        .then(() => {
          state = "attempted";
          recoveredAt = now();
          deps.onRecovered();
        })
        .catch(() => {
          state = "attempted";
          deps.signOut();
        });
    },
  };
}
